// Work Orders › Internal: the marina's own work, nobody to bill. An operation is internal
// when its Category is Internal or it's on a piece of the marina's equipment
// (Customer_ID 'equipment', equipmentWork.js). One card per work order with its internal
// operations; open work first. Click the WO number to open the work order, an operation
// to open its panel, the unit chip for the unit's page (app.js [data-equipment-unit]).
// Live: redraws in place when jobs change (docs/live-updates-plan.md).

import { idbCursorAll, escapeHTML, woLabel } from './utilities';
import { patchHTML, liveRefresher, applyLive, onLocalDataChanged } from './liveView';
import { techsLabel } from './techNames';
import { EQUIPMENT_CUSTOMER, INTERNAL_CATEGORY } from './equipmentWork';

let deps = { opCheck: () => {}, openWorkOrder: () => {} };
/** app.js hands in opCheck and openWorkOrder (circular import otherwise). */
export function setInternalViewDeps(d) { deps = { ...deps, ...d }; }

const DONE = new Set(['Ready to Bill', 'Complete']);
const stage = j => (j.Status === 'Closed' ? 'closed' : DONE.has(j.Status) ? 'done' : 'open');
const FILTERS = [['open', 'Open'], ['done', 'Ready to bill / complete'], ['closed', 'Closed']];
const EMPTY = {
  open: 'No open internal work. Equipment work orders and Internal operations show here.',
  done: 'No internal work waiting to be closed.',
  closed: 'No closed internal work on this device yet. Older work is under the Closed tab.',
};
let filter = 'open';
let jobsById = new Map();
let lastJobs = [];

export const isInternalOp = j => !!j && (j.Category === INTERNAL_CATEGORY || j.Customer_ID === EQUIPMENT_CUSTOMER);

const viewShown = () => {
  const v = document.getElementById('internal-view');
  return !!v && v.style.display !== 'none';
};

const dayLabel = ds => new Date(`${ds.slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const isDay = v => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v);

async function loadJobs() {
  const jobs = (await idbCursorAll('jobData')).filter(j => j && j.id && j.Status !== 'Declined' && j.Category !== 'Declined' && isInternalOp(j));
  jobsById = new Map(jobs.map(j => [j.id, j]));
  return jobs;
}

function matchesSearch(j, q) {
  if (!q) return true;
  return [j.Work_Order, woLabel(j.Work_Order), j.Boat_Name, j.Customer_Name, j.Equipment_Unit, j.Short_Description, j.Description, j.Techs, j.Status]
    .join(' ').toLowerCase().includes(q);
}

function groups(jobs) {
  const byWo = new Map();
  jobs.forEach(j => {
    const wo = String(j.Work_Order || '—');
    if (!byWo.has(wo)) byWo.set(wo, []);
    byWo.get(wo).push(j);
  });
  return [...byWo.entries()].map(([wo, ops]) => ({
    wo, ops: ops.sort((a, b) => String(a.Schedule_Date || a.Start_Date || '').localeCompare(String(b.Schedule_Date || b.Start_Date || ''))),
    open: ops.some(o => stage(o) === 'open'),
  })).sort((a, b) => (b.open - a.open) || b.wo.localeCompare(a.wo, undefined, { numeric: true }));
}

function opHTML(j) {
  const techs = techsLabel(j.Techs);
  const when = techs && isDay(j.Schedule_Date) ? `, from ${dayLabel(j.Schedule_Date)}` : '';
  return `
    <div class="iw-op${stage(j) === 'open' ? '' : ' is-done'}" data-key="${escapeHTML(j.id)}" data-op-id="${escapeHTML(j.id)}" tabindex="0">
      <div class="iw-op-main">
        <div class="iw-op-title">${escapeHTML(j.Short_Description || j.Description || 'Operation')}${j.Priority === 'High' ? ' <span class="iw-high">HIGH</span>' : ''}</div>
        <div class="iw-op-sub">${techs ? escapeHTML(techs + when) : '<span class="iw-unassigned">Unassigned</span>'}${Number(j.Estimated_Hours) > 0 ? ` · est. ${escapeHTML(String(Number(j.Estimated_Hours)))} h` : ''}</div>
      </div>
      <span class="iw-status">${escapeHTML(j.Status || '')}</span>
    </div>`;
}

function groupHTML(g) {
  const first = g.ops[0];
  const unit = g.ops.find(o => o.Equipment_Unit)?.Equipment_Unit;
  const what = unit ? (first.Boat_Name || unit) : [first.Customer_Name, first.Boat_Name].filter(Boolean).join(' · ');
  return `
    <section class="iw-wo" data-key="wo-${escapeHTML(g.wo)}">
      <div class="iw-wo-head">
        <button type="button" class="iw-wo-num" data-open-wo="${escapeHTML(g.wo)}">${escapeHTML(woLabel(g.wo))}</button>
        <span class="iw-wo-what">${escapeHTML(what || 'Internal')}</span>
        ${unit ? `<button type="button" class="iw-unit" data-equipment-unit="${escapeHTML(unit)}">Equipment · ${escapeHTML(unit)} ›</button>` : '<span class="iw-tag">Internal</span>'}
        <span class="iw-wo-count">${g.ops.length} op${g.ops.length === 1 ? '' : 's'}</span>
      </div>
      ${g.ops.map(opHTML).join('')}
    </section>`;
}

function draw(jobs) {
  const q = (document.getElementById('internal-search')?.value || '').trim().toLowerCase();
  const found = jobs.filter(j => matchesSearch(j, q));
  const counts = { open: 0, done: 0, closed: 0 };
  found.forEach(j => { counts[stage(j)]++; });
  const shown = found.filter(j => stage(j) === filter);
  patchHTML(document.getElementById('iw-bar'), FILTERS.map(([k, label]) =>
    `<button type="button" class="sw-stage-chip${filter === k ? ' active' : ''}" data-key="chip-${k}" data-iw-filter="${k}">${escapeHTML(label)} <span class="sw-stage-count">${counts[k]}</span></button>`).join('')
    + '<span class="iw-hint">Equipment work and anything filed under the Internal category.</span>');
  patchHTML(document.getElementById('iw-list'), shown.length ? groups(shown).map(groupHTML).join('')
    : `<div class="sw-empty">${q ? 'No internal work matches the search.' : EMPTY[filter]}</div>`);
  wire();
}

function wire() {
  document.querySelectorAll('#iw-bar [data-iw-filter]').forEach(btn => {
    btn.onclick = () => { filter = btn.dataset.iwFilter; draw(lastJobs); };
  });
  document.querySelectorAll('#iw-list [data-open-wo]').forEach(btn => {
    btn.onclick = () => deps.openWorkOrder(btn.dataset.openWo);
  });
  document.querySelectorAll('#iw-list .iw-op').forEach(row => {
    const open = () => {
      const job = jobsById.get(row.dataset.opId);
      if (job) deps.opCheck(null, job);
    };
    row.onclick = open;
    row.onkeydown = (e) => { if (e.key === 'Enter') open(); };
  });
}

export async function renderInternalView() {
  if (!document.getElementById('iw-list')) return;
  lastJobs = await loadJobs();
  draw(lastJobs);
}

document.getElementById('internal-search')?.addEventListener('input', () => draw(lastJobs));

const live = liveRefresher({
  refresh: async (isBusy) => {
    if (!viewShown()) return;
    const jobs = await loadJobs();
    if (isBusy()) { setTimeout(live, 0); return; }
    lastJobs = jobs;
    applyLive(() => draw(jobs));
  },
});
onLocalDataChanged(live);
